import { Form, useForm } from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { ApiError } from "@portal/lib/ky";
import { useVerifyOtp } from "@portal/queries/login";
import { DefaultErrorResponse, LOGIN_ERROR_STATUS } from "@portal/types/common";
import { LoginDTO } from "@portal/types/entities/login";
import { useQueryClient } from "@tanstack/react-query";
import OTPInput from "react-otp-input";
import { Button } from "../../../../BaseComponents/Button/AntButton";
import { Flex } from "../../../../BaseComponents/Flex/Flex";
import { RendererInput } from "../../../../BaseComponents/Input/type";
import { CountDownProps } from "../../../../BaseComponents/Statistic/type";
import { TypographyText } from "../../../../BaseComponents/Typography/AntTypography";
import { AlertStatus } from "../AlterInfo";
import { StyledCountdown, StyledInputNumber } from "./style";

const t = {
  title: "remaining to enter the PIN",
  submit: "Submit",
  heading: "Enter the PIN you received",
};
interface InputPinPros {
  onChange?: (value: string) => void;
  value?: string;
  error?: boolean;
}

interface EnterPinProps {
  updateStatus: (status: AlertStatus) => void;
  countDownValue: CountDownProps["value"];
  error?: boolean;
  handleError(
    error: ApiError<DefaultErrorResponse<LOGIN_ERROR_STATUS>>
  ): Promise<void>;

  errorStatus?: LOGIN_ERROR_STATUS;
  extra?: React.ReactNode;
}
export default function EnterPin(props: Readonly<EnterPinProps>) {
  const {
    updateStatus,
    countDownValue,
    error,
    handleError,
    errorStatus,
    extra,
  } = props;
  const [form] = useForm<Pick<LoginDTO, "otp">>();
  const queryClient = useQueryClient();
  const verifyOTP = useVerifyOtp();

  const isDisabled =
    errorStatus === "OTP_LIMIT_REACHED" || errorStatus === "OTP_WAIT_TIME";
  function handleFinish() {
    // updateStatus("warning");
  }
  function handleSubmit() {
    const currentData = queryClient.getQueryState<
      Pick<LoginDTO, "email" | "user_id">
    >(["send-otp"]);
    const fs = currentData?.data as LoginDTO;
    form.validateFields().then((value) => {
      verifyOTP
        .mutateAsync({ ...fs, ...value })
        .then(() => {
          updateStatus("success");
        })
        .catch(handleError);
    });
  }

  const SessionCount = (
    <StyledCountdown
      value={countDownValue}
      format="mm:ss"
      title={t.title}
      onFinish={handleFinish}
      layout="l2"
    />
  );

  const InputArea = (
    <Flex vertical align="start">
      <StyledFormItem name="otp">
        <InputPin error={error} />
      </StyledFormItem>
      {SessionCount}
      {extra}
    </Flex>
  );

  const SubmitButton = (
    <Button
      size="large"
      type="primary"
      onClick={handleSubmit}
      htmlType="submit"
      loading={verifyOTP.isLoading}
      disabled={isDisabled}
    >
      {t.submit}
    </Button>
  );
  const Body = (
    <Form form={form}>
      <Flex align="start" space="12">
        {InputArea}
        {SubmitButton}
      </Flex>
    </Form>
  );

  const Heading = <TypographyText fs="14">{t.heading}</TypographyText>;

  return (
    <Flex vertical align="start">
      {Heading}
      {Body}
    </Flex>
  );
}

function InputPin(props: InputPinPros) {
  const { error } = props;
  function handleChange(value: string) {
    props.onChange?.(value);
  }

  function renderInput(...args: RendererInput) {
    const [props] = args;
    return (
      <StyledInputNumber
        {...props}
        placeholder="-"
        min={0}
        max={9}
        maxLength={1}
        type="number"
        state={error ? "error" : undefined}
      />
    );
  }
  return (
    <OTPInput
      value={props.value}
      onChange={handleChange}
      numInputs={6}
      renderInput={renderInput}
      containerStyle={{ gap: "var(--space-8)" }}
    />
  );
}

export { InputPin };
